import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ScanUploadModal from '../components/common/ScanUploadModal';
import EditScanModal from '../components/common/EditScanModal';
import DeleteConfirmModal from '../components/common/DeleteConfirmModal';
import ClearAllModal from '../components/common/ClearAllModal';
import ProfileAvatar from '../components/common/ProfileAvatar';
import patientService from '../services/patientService';
import caseService from '../services/caseService';
import inferenceService from '../services/inferenceService';
import JSZip from 'jszip';
import reportService from '../services/reportService';
import { useAuth } from '../context/AuthContext';
import noDataImage from '../assets/no-data.png';
import '../styles/PatientAddingPage.css';

const MAX_PATIENTS = 10;

export default function PatientAddingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const inputRef = useRef(null);

  const { user } = useAuth();
  const isSuperadmin = user?.role === 'superadmin';

  const storageKey      = user?.username ? `patientAddingList_${user.username}` : null;
  const batchStorageKey = user?.username ? `currentBatchId_${user.username}`    : null;

  const [inputId,  setInputId]  = useState('');
  const [adding,   setAdding]   = useState(false);
  const [addError, setAddError] = useState('');

  const [patients,       setPatients]       = useState([]);
  const [currentBatchId, setCurrentBatchId] = useState(null);
  const [dataLoaded,     setDataLoaded]     = useState(false);
  const [downloading,    setDownloading]    = useState(false);

  // Active nav: filled dark-pink bg + white text; inactive: white bg + pink border + pink text
  const navCls = (path) =>
    `pap__nav-btn${location.pathname === path ? ' pap__nav-btn--active' : ''}`;

  useEffect(() => {
    if (!storageKey || dataLoaded) return;
    const saved = sessionStorage.getItem(storageKey);
    if (saved) {
      try {
        setPatients(JSON.parse(saved));
      } catch (e) {}
    }
    const savedBatch = sessionStorage.getItem(batchStorageKey);
    if (savedBatch) setCurrentBatchId(savedBatch);
    setDataLoaded(true);
  }, [storageKey, batchStorageKey, dataLoaded]);

  useEffect(() => {
    if (!storageKey || !dataLoaded) return;
    const safePatients = patients.map(p => {
      const { leftFiles, rightFiles, ...rest } = p;
      return rest;
    });
    sessionStorage.setItem(storageKey, JSON.stringify(safePatients));
  }, [patients, storageKey, dataLoaded]);

  useEffect(() => {
    if (!batchStorageKey || !dataLoaded) return;
    if (currentBatchId) {
      sessionStorage.setItem(batchStorageKey, currentBatchId);
    } else {
      sessionStorage.removeItem(batchStorageKey);
    }
  }, [currentBatchId, batchStorageKey, dataLoaded]);

  const [scanTarget,   setScanTarget]   = useState(null);
  const [editTarget,   setEditTarget]   = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showClearAll, setShowClearAll] = useState(false);
  const [analysing,    setAnalysing]    = useState(false);

  /* ─────────────────────────────────────────────
     1. ADD PATIENT
  ───────────────────────────────────────────── */
  const handleAdd = async () => {
    const trimmed = inputId.trim();
    setAddError('');

    if (!trimmed) { setAddError('Please enter a Patient ID.'); return; }
    if (patients.length >= MAX_PATIENTS) { setAddError('Maximum 10 patients reached.'); return; }
    if (patients.find(p => p.id === trimmed)) { setAddError('Patient already in queue.'); return; }

    setAdding(true);
    try {
      const result = await patientService.lookup(trimmed);

      if (result.status === 404 || !result.found) {
        setAddError(result.message || `Patient ID "${trimmed}" not found.`);
        return;
      }
      if (result.status === 503 || result.serviceDown) {
        setScanTarget({
          id: trimmed,
          name: '',
          patientData: null,
          status: 'pending',
        });
        setInputId('');
        return;
      }

      const pd = result.patient;
      setScanTarget({
        id: trimmed,
        name: pd.full_name || pd.name || trimmed,
        patientData: pd,
        status: 'pending',
      });
      setInputId('');
    } catch (e) {
      setAddError('Network error. Please try again.');
    } finally {
      setAdding(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  const handleKeyDown = e => { if (e.key === 'Enter' && !adding) handleAdd(); };

  /* ─────────────────────────────────────────────
     2. SCAN UPLOAD & INSTANT ANALYSE
  ───────────────────────────────────────────── */
  const handleAddToQueue = async (patientWithFiles) => {
    const newPatient = { ...patientWithFiles, status: 'analysing' };
    setPatients(prev => [...prev, newPatient]);
    setScanTarget(null);

    setAnalysing(true);
    let activeBatchId = currentBatchId;

    try {
      const fd = new FormData();
      fd.append('patient_id', newPatient.id);
      if (activeBatchId) fd.append('batch_id', activeBatchId);
      fd.append('scan_date', new Date().toISOString().split('T')[0]);

      const pd = newPatient.patientData || {};
      const patientName = pd.full_name || newPatient.name || '';
      if (patientName) {
        fd.append('full_name', patientName);
        fd.append('patient_name', patientName);
      }
      if (pd.age)              fd.append('age',              pd.age);
      if (pd.gender)           fd.append('gender',           pd.gender);
      if (pd.mobile)           fd.append('mobile',           pd.mobile);
      if (pd.referring_doctor) fd.append('referring_doctor', pd.referring_doctor);
      if (pd.registered_at)   fd.append('scan_center',      pd.registered_at);

      (newPatient.leftFiles || []).forEach((file, i) => {
        fd.append(`left_image_${i + 1}`, file);
      });
      (newPatient.rightFiles || []).forEach((file, i) => {
        fd.append(`right_image_${i + 1}`, file);
      });

      const result = await caseService.batchAdd(fd);

      if (result.batch_id && !activeBatchId) {
        activeBatchId = result.batch_id;
        setCurrentBatchId(activeBatchId);
      }

      if (result.case_id || result.id) {
        let caseId = result.case_id || result.id;
        try {
          await new Promise(resolve => setTimeout(resolve, 3000));
          await inferenceService.runAnalysis(caseId);
          setPatients(prev =>
            prev.map(p => p.id === newPatient.id
              ? { ...p, status: 'done', caseId, analysisComplete: true }
              : p
            )
          );
        } catch (analysisError) {
          console.error(`AI analysis failed for patient ${newPatient.id}:`, analysisError);
          setPatients(prev =>
            prev.map(p => p.id === newPatient.id
              ? { ...p, status: 'done', caseId, analysisError: true }
              : p
            )
          );
        }
      } else {
        const fallbackId = result.case_id || result.id;
        setPatients(prev =>
          prev.map(p => p.id === newPatient.id
            ? { ...p, status: 'done', caseId: fallbackId, result }
            : p
          )
        );
      }
    } catch (e) {
      console.error(`Error analyzing patient ${newPatient.id}:`, e);
      setPatients(prev =>
        prev.map(p => p.id === newPatient.id
          ? { ...p, status: 'error', errorMsg: e.response?.data?.error || e.message || 'Upload failed' }
          : p
        )
      );
    }
    setAnalysing(false);
  };

  const handleView = (patient) => {
    if (patient.status !== 'done') return;
    navigate(`/cases/${patient.caseId}/report`);
  };

  const goToDashboard = () => {
    const hasPendingPatients = patients.some(p => p.status !== 'done');
    if (!hasPendingPatients) {
      if (storageKey) sessionStorage.removeItem(storageKey);
      if (batchStorageKey) sessionStorage.removeItem(batchStorageKey);
      setPatients([]);
      setCurrentBatchId(null);
    }
    navigate('/cases');
  };

  const handleEditComplete = (updatedPatient) => {
    setPatients(prev =>
      prev.map(p => p.id === updatedPatient.id ? { ...updatedPatient } : p)
    );
    setEditTarget(null);
  };

  const doDelete = async () => {
    const p = deleteTarget;
    if (p.status === 'done' && p.caseId) {
      try {
        await caseService.batchRemoveItem(p.caseId, p.id);
      } catch (e) {
        console.error('Rollback error:', e.message);
      }
    }
    setPatients(prev => prev.filter(pt => pt.id !== p.id));
    setDeleteTarget(null);
  };

  const doClearAll = async () => {
    const done = patients.filter(p => p.status === 'done' && p.caseId);
    for (const p of done) {
      try { await caseService.batchRemoveItem(p.caseId, p.id); } catch { }
    }
    setPatients([]);
    setCurrentBatchId(null);
    setShowClearAll(false);
  };

  /* ─────────────────────────────────────────────
     3. DOWNLOAD ALL PDFs AS ZIP
  ───────────────────────────────────────────── */
  const downloadAll = async () => {
    const completedPatients = patients.filter(p => p.status === 'done' && p.caseId);

    if (completedPatients.length === 0) {
      alert('No completed cases to download. Please wait for analysis to finish.');
      return;
    }

    setDownloading(true);
    try {
      const zip = new JSZip();
      let successCount = 0;
      let failCount = 0;

      for (const patient of completedPatients) {
        try {
          const blob = await reportService.getPdfBlob(patient.caseId);
          const safeName = (patient.name || patient.id).replace(/[^a-zA-Z0-9_-]/g, '_');
          const fileName = `BI_RADS_Report_${safeName}_${patient.id}.pdf`;
          zip.file(fileName, blob);
          successCount++;
        } catch (error) {
          console.warn(`Failed to fetch PDF for patient ${patient.id}:`, error);
          failCount++;
        }
      }

      if (successCount === 0) {
        alert('Failed to download any patient reports. Please try again.');
        return;
      }

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Patient_Reports_${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      const message = failCount > 0
        ? `Downloaded ${successCount} reports (${failCount} failed)`
        : `Successfully downloaded ${successCount} patient reports`;
      alert(message);

    } catch (error) {
      console.error('Error downloading reports:', error);
      alert('An error occurred while preparing the download. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  /* ─────────────────────────────────────────────
     HELPERS
  ───────────────────────────────────────────── */
  const MAX_PER_COLUMN = 5;
  const col1 = patients.slice(0, MAX_PER_COLUMN);
  const col2 = patients.slice(MAX_PER_COLUMN);

  const StatusIcon = ({ status }) => {
    if (status === 'done')
      return <i className="fa-solid fa-circle-check pap__icon-done" />;
    if (status === 'analysing')
      return <i className="fa-solid fa-spinner fa-spin pap__icon-spin" />;
    if (status === 'error')
      return <i className="fa-solid fa-circle-exclamation pap__icon-error" />;
    return <i className="fa-regular fa-circle pap__icon-pending" />;
  };

  const PatientRow = ({ patient }) => {
    const isDone      = patient.status === 'done';
    const isAnalysing = patient.status === 'analysing';

    return (
      <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
        <span className="pap__status-icon-wrapper">
          <StatusIcon status={patient.status} />
        </span>

        <input
          className="form-control form-control-sm pap__id-input pap__id-input--small"
          value={patient.id}
          readOnly
        />
        <input
          className="form-control form-control-sm pap__id-input pap__input-name"
          value={patient.name || '—'}
          readOnly
        />

        <button
          className={`btn btn-sm pap__btn-view ${!isDone ? 'pap__btn-view--disabled' : ''}`}
          onClick={() => handleView(patient)}
          disabled={!isDone}
          title={isDone ? 'View result' : 'Analysis not yet done'}
        >
          View
        </button>

        <button
          className="btn btn-sm p-0 pap__icon-btn"
          title="Edit / re-upload scans"
          disabled={isAnalysing}
          onClick={() => setEditTarget(patient)}
        >
          <i className="fa-solid fa-pen text-secondary" />
        </button>

        <button
          className="btn btn-sm p-0 pap__icon-btn"
          title="Delete"
          disabled={isAnalysing}
          onClick={() => setDeleteTarget(patient)}
        >
          <i className="fa-solid fa-trash-can text-danger" />
        </button>
      </div>
    );
  };

  /* ─────────────────────────────────────────────
     RENDER
  ───────────────────────────────────────────── */
  return (
    <div className="pap__root">

      <nav className="navbar pap__navbar px-3 px-md-4">
        <span className="navbar-brand text-white fw-600 fs-5 mb-0">
          {isSuperadmin ? 'Patient Adding Page' : 'Patient Adding Page'}
        </span>
        <div className="d-flex align-items-center gap-2">
          {isSuperadmin && (
            <button className={navCls('/admin')} onClick={() => navigate('/admin')}>
              View Admins
            </button>
          )}
          <button className={navCls('/patients')} onClick={() => navigate('/patients')}>
            Start Analysis
          </button>
          <button className={navCls('/cases')} onClick={goToDashboard}>
            Dashboard
          </button>
          <ProfileAvatar />
        </div>
      </nav>

      <div className="container-fluid px-3 px-md-4 pt-4">

        <div className="d-flex align-items-center justify-content-between mb-2">
          <label className="fw-bold mb-0 d-block">Enter Patient Id NO</label>
        </div>
        <div className="d-flex align-items-center gap-0 mt-2 pap__add-input-wrapper">
          <input
            ref={inputRef}
            type="text"
            className="form-control pap__add-input pap__add-input-left"
            placeholder="Enter a Patient Id"
            value={inputId}
            onChange={e => { setInputId(e.target.value); setAddError(''); }}
            onKeyDown={handleKeyDown}
            disabled={adding || patients.length >= MAX_PATIENTS}
          />
          <button
            className="btn pap__btn-add pap__btn-add-right"
            onClick={handleAdd}
            disabled={adding || !inputId.trim() || patients.length >= MAX_PATIENTS}
          >
            {adding
              ? <><i className="fa-solid fa-spinner fa-spin me-1" />Adding…</>
              : 'Add Patient'}
          </button>
        </div>

        {addError && (
          <div className="pap__add-error mt-2">
            <i className="fa-solid fa-triangle-exclamation me-1" />{addError}
          </div>
        )}

        <div className="d-flex align-items-start justify-content-between mt-4 mb-2 flex-wrap gap-2">
          <div>
            <div className="fw-bold">
              {patients.length > 0 ? 'Patient Added list:' : 'Patient Added list'}
            </div>
            <div className="text-muted small">
              No of data : {patients.length}/{MAX_PATIENTS}
            </div>
          </div>

          {patients.length > 0 && (
            <div className="d-flex gap-2 flex-wrap align-items-center">
              <button
                className="btn btn-outline-danger btn-sm px-3"
                onClick={() => setShowClearAll(true)}
                disabled={analysing || downloading}
              >
                <i className="fa-solid fa-xmark me-1" />Clear All
              </button>
              <button
                className="btn btn-sm pap__btn-download px-3"
                onClick={downloadAll}
                disabled={analysing || downloading}
              >
                {downloading
                  ? <><i className="fa-solid fa-spinner fa-spin me-1" />Downloading…</>
                  : <><i className="fa-solid fa-download me-1" />Download All</>
                }
              </button>
            </div>
          )}
        </div>

        {patients.length === 0 ? (
          <div className="text-center py-5 mt-3">
            <img
              src={noDataImage}
              alt="No Data Found"
              style={{ width: '220px', height: 'auto' }}
            />
          </div>
        ) : (
          <div className="row g-0">
            <div className="col-12 col-lg-6 pe-lg-4">
              {col1.map(p => <PatientRow key={p.id} patient={p} />)}
            </div>
            <div className="col-12 col-lg-6 ps-lg-2">
              {col2.map(p => <PatientRow key={p.id} patient={p} />)}
            </div>
          </div>
        )}
      </div>

      {scanTarget && (
        <ScanUploadModal
          patient={scanTarget}
          onClose={() => setScanTarget(null)}
          onAddToQueue={handleAddToQueue}
        />
      )}

      {editTarget && (
        <EditScanModal
          patient={editTarget}
          onClose={() => setEditTarget(null)}
          onComplete={handleEditComplete}
        />
      )}

      {deleteTarget && (
        <DeleteConfirmModal
          patientName={deleteTarget.name || deleteTarget.id}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={doDelete}
        />
      )}

      {showClearAll && (
        <ClearAllModal
          patients={patients}
          onCancel={() => setShowClearAll(false)}
          onConfirm={doClearAll}
        />
      )}

    </div>
  );
}